import React, { useEffect, useState, useCallback } from "react";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { fmtMoney } from "../lib/format";
import { Container, Anchor, Send, Loader2, CheckCircle2, XCircle, BadgeDollarSign, Clock, MapPin } from "lucide-react";

const STATUS_BADGE = {
  pending: "bg-amber-100 text-amber-700",
  quoted: "bg-blue-100 text-blue-700",
  accepted: "bg-emerald-100 text-emerald-700",
  declined: "bg-slate-100 text-slate-500",
  expired: "bg-slate-100 text-slate-500",
};

function IssueForm({ quote, onDone }) {
  const [price, setPrice] = useState(quote.estimate || "");
  const [days, setDays] = useState(14);
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    await api.post(`/quotes/${quote.quote_id}/issue`, { quoted_price: Number(price), valid_days: Number(days), admin_notes: notes || null });
    setBusy(false); onDone();
  };
  return (
    <form onSubmit={submit} className="mt-3 pt-3 border-t border-slate-100 grid sm:grid-cols-4 gap-2 items-end" data-testid={`issue-form-${quote.quote_id}`}>
      <div>
        <label className="label-base">Quoted price ($)</label>
        <input type="number" className="input-base" value={price} onChange={(e) => setPrice(e.target.value)} required data-testid={`issue-price-${quote.quote_id}`} />
      </div>
      <div>
        <label className="label-base">Valid (days)</label>
        <input type="number" className="input-base" value={days} onChange={(e) => setDays(e.target.value)} />
      </div>
      <input className="input-base sm:col-span-1" placeholder="Notes (optional)" value={notes} onChange={(e) => setNotes(e.target.value)} />
      <button className="btn-primary" disabled={busy} data-testid={`issue-submit-${quote.quote_id}`}>
        {busy ? <Loader2 size={14} className="animate-spin" /> : <BadgeDollarSign size={14} />} Issue quote
      </button>
    </form>
  );
}

export default function Quotes() {
  const { user } = useAuth();
  const isAdmin = user.role === "super_admin" || user.role === "ops_admin";
  const canView = isAdmin || user.role === "shipper";
  const [opts, setOpts] = useState(null);
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");
  const [f, setF] = useState({
    origin_dock_code: "", dest_type: "dock", dest_dock_code: "",
    dest_zip: "", dest_city: "", dest_state: "",
    container_size: "", quantity: 1, commodity: "", weight: "", ready_date: "", notes: "",
  });

  const refresh = useCallback(async () => {
    try {
      const r = await api.get("/quotes");
      setQuotes(r.data);
    } catch {
      setQuotes([]);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    if (!canView) { setLoading(false); return; }
    if (user.role === "shipper") api.get("/quotes/options").then((r) => setOpts(r.data));
    refresh();
  }, [refresh, canView, user.role]);

  const submit = async (e) => {
    e.preventDefault(); setSubmitting(true); setErr("");
    try {
      const payload = { ...f, quantity: Number(f.quantity), weight: f.weight ? Number(f.weight) : null };
      await api.post("/quotes", payload);
      setF({ ...f, commodity: "", weight: "", notes: "" });
      await refresh();
    } catch (e2) {
      setErr(e2.response?.data?.detail || "Could not submit quote request");
    }
    setSubmitting(false);
  };

  if (!canView) {
    return (
      <div className="card-base p-10 text-center max-w-lg mx-auto" data-testid="quotes-denied">
        <Container size={30} className="text-slate-300 mx-auto mb-3" />
        <div className="font-display text-xl font-bold text-navy">Container quotes</div>
        <p className="text-slate-500 mt-1 text-sm">This area is available to shippers and admins only.</p>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div>
        <div className="text-[11px] tracking-[0.2em] uppercase text-gold font-bold mb-2">Container drayage</div>
        <h1 className="font-display text-4xl font-bold tracking-tighter text-navy">Container shipping quotes</h1>
        <p className="text-slate-500 mt-2">
          {isAdmin ? "Review requests from shippers and issue official quotes." : "Get instant estimates for any container size from docks across the United States."}
        </p>
      </div>

      {/* Shipper request form */}
      {!isAdmin && opts && (
        <form onSubmit={submit} className="card-base p-6 space-y-4" data-testid="quote-request-form">
          <div className="font-display text-lg font-bold text-navy flex items-center gap-2"><Container size={18} className="text-gold" /> Request a quote</div>
          {err && <div className="text-sm text-red-600 font-semibold" data-testid="quote-error">{err}</div>}
          <div className="grid md:grid-cols-3 gap-3">
            <div>
              <label className="label-base">Origin dock</label>
              <select className="input-base" value={f.origin_dock_code} onChange={(e) => setF({ ...f, origin_dock_code: e.target.value })} required data-testid="q-origin">
                <option value="">Select port…</option>
                {opts.docks.map((d) => <option key={d.code} value={d.code}>{d.name} ({d.state})</option>)}
              </select>
            </div>
            <div>
              <label className="label-base">Destination type</label>
              <select className="input-base" value={f.dest_type} onChange={(e) => setF({ ...f, dest_type: e.target.value })} data-testid="q-dest-type">
                <option value="dock">Another dock/port</option>
                <option value="address">Inland address (ZIP)</option>
              </select>
            </div>
            {f.dest_type === "dock" ? (
              <div>
                <label className="label-base">Destination dock</label>
                <select className="input-base" value={f.dest_dock_code} onChange={(e) => setF({ ...f, dest_dock_code: e.target.value })} required data-testid="q-dest-dock">
                  <option value="">Select port…</option>
                  {opts.docks.map((d) => <option key={d.code} value={d.code}>{d.name} ({d.state})</option>)}
                </select>
              </div>
            ) : (
              <div className="grid grid-cols-3 gap-2">
                <input className="input-base col-span-1" placeholder="ZIP" value={f.dest_zip} onChange={(e) => setF({ ...f, dest_zip: e.target.value })} required data-testid="q-dest-zip" />
                <input className="input-base col-span-1" placeholder="City" value={f.dest_city} onChange={(e) => setF({ ...f, dest_city: e.target.value })} />
                <input className="input-base col-span-1" placeholder="St" maxLength={2} value={f.dest_state} onChange={(e) => setF({ ...f, dest_state: e.target.value.toUpperCase() })} />
              </div>
            )}
            <div>
              <label className="label-base">Container size</label>
              <select className="input-base" value={f.container_size} onChange={(e) => setF({ ...f, container_size: e.target.value })} required data-testid="q-size">
                <option value="">Select size…</option>
                {opts.container_sizes.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
              </select>
            </div>
            <div>
              <label className="label-base">Quantity</label>
              <input type="number" min="1" className="input-base" value={f.quantity} onChange={(e) => setF({ ...f, quantity: e.target.value })} data-testid="q-qty" />
            </div>
            <div>
              <label className="label-base">Ready date</label>
              <input type="date" className="input-base" value={f.ready_date} onChange={(e) => setF({ ...f, ready_date: e.target.value })} data-testid="q-ready" />
            </div>
            <div>
              <label className="label-base">Commodity</label>
              <input className="input-base" placeholder="e.g. Electronics" value={f.commodity} onChange={(e) => setF({ ...f, commodity: e.target.value })} data-testid="q-commodity" />
            </div>
            <div>
              <label className="label-base">Weight (lbs)</label>
              <input type="number" className="input-base" value={f.weight} onChange={(e) => setF({ ...f, weight: e.target.value })} data-testid="q-weight" />
            </div>
            <div className="flex items-end">
              <button className="btn-primary w-full" disabled={submitting} data-testid="q-submit">
                {submitting ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />} Get estimate
              </button>
            </div>
          </div>
        </form>
      )}

      {/* Quotes list */}
      <div>
        <div className="font-display text-xl font-bold text-navy mb-4">{isAdmin ? "All quote requests" : "Your quotes"}</div>
        {loading ? (
          <div className="text-slate-500 flex items-center gap-2"><Loader2 size={16} className="animate-spin" /> Loading…</div>
        ) : quotes.length === 0 ? (
          <div className="card-base p-10 text-center text-slate-500" data-testid="no-quotes">No quotes yet.</div>
        ) : (
          <div className="space-y-3" data-testid="quotes-list">
            {quotes.map((q) => (
              <div key={q.quote_id} className="card-base p-5" data-testid={`quote-${q.quote_id}`}>
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-display text-lg font-bold text-navy">{q.quantity}× {q.container_label}</span>
                      <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${STATUS_BADGE[q.status]}`} data-testid={`quote-status-${q.quote_id}`}>{q.status}</span>
                    </div>
                    <div className="text-sm text-slate-600 mt-1 flex items-center gap-1.5">
                      <Anchor size={13} className="text-gold" /> {q.origin_label}
                      <span className="text-slate-300">→</span>
                      <MapPin size={13} className="text-gold" /> {q.dest_label}
                    </div>
                    <div className="text-xs text-slate-400 mt-1">
                      {q.distance_mi ? `${q.distance_mi} mi` : "Distance N/A"}
                      {q.commodity ? ` · ${q.commodity}` : ""}{q.weight ? ` · ${Number(q.weight).toLocaleString()} lbs` : ""}
                      {isAdmin ? ` · ${q.shipper_name}` : ""}
                    </div>
                  </div>
                  <div className="text-right">
                    {q.status === "quoted" || q.status === "accepted" ? (
                      <>
                        <div className="text-[10px] uppercase tracking-wider font-bold text-slate-400">Quoted</div>
                        <div className="font-display text-2xl font-bold text-navy" data-testid={`quote-price-${q.quote_id}`}>{fmtMoney(q.quoted_price)}</div>
                        {q.valid_until && <div className="text-[11px] text-slate-400 flex items-center gap-1 justify-end"><Clock size={10} /> valid till {new Date(q.valid_until).toLocaleDateString()}</div>}
                      </>
                    ) : (
                      <>
                        <div className="text-[10px] uppercase tracking-wider font-bold text-slate-400">Instant estimate</div>
                        <div className="font-display text-xl font-bold text-slate-500">{q.estimate ? fmtMoney(q.estimate) : "—"}</div>
                      </>
                    )}
                  </div>
                </div>
                {q.admin_notes && <div className="text-xs text-slate-500 mt-2 italic">“{q.admin_notes}”</div>}
                {q.status === "accepted" && q.load_id && (
                  <a href={`/app/loads/${q.load_id}`} className="inline-flex items-center gap-1 text-sm font-bold text-gold mt-2" data-testid={`view-load-${q.quote_id}`}>
                    Booked as load {q.load_id} →
                  </a>
                )}

                {/* Admin issue action */}
                {isAdmin && (q.status === "pending") && (
                  <IssueForm quote={q} onDone={refresh} />
                )}

                {/* Shipper accept/decline */}
                {!isAdmin && q.status === "quoted" && (
                  <div className="flex gap-2 mt-3 pt-3 border-t border-slate-100">
                    <button onClick={async () => { await api.post(`/quotes/${q.quote_id}/accept`); refresh(); }} className="btn-primary" data-testid={`accept-${q.quote_id}`}>
                      <CheckCircle2 size={15} /> Accept quote
                    </button>
                    <button onClick={async () => { await api.post(`/quotes/${q.quote_id}/decline`); refresh(); }} className="btn-secondary" data-testid={`decline-${q.quote_id}`}>
                      <XCircle size={15} /> Decline
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
